/**
 * OnboardingChecklist.jsx
 * Shows onboarding progress widget in the dashboard for new merchants.
 */
import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { CheckCircle2, Circle, ChevronDown, ChevronUp, Zap } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function OnboardingChecklist() {
  const [progress, setProgress] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/merchant/onboarding");
        // Only show if not completed
        if (data.step !== "completed") {
          setProgress(data);
        }
      } catch {}
    })();
  }, []);

  if (!progress) return null;

  const steps = [
    {
      key: "store_connected",
      done: progress.store_connected,
      label: "ربط متجر سلة",
      action: () => navigate("/settings"),
      actionLabel: "اذهب للإعدادات",
    },
    {
      key: "first_analysis_done",
      done: progress.first_analysis_done,
      label: "تحليل أداء المتجر",
      action: () => navigate("/optimizer"),
      actionLabel: "ابدأ التحليل",
    },
    {
      key: "qoyod_connected",
      done: progress.qoyod_connected,
      label: "ربط قيود",
      action: () => navigate("/settings"),
      actionLabel: "ربط قيود",
    },
    {
      key: "sync_configured",
      done: progress.sync_configured,
      label: "إعداد المزامنة التلقائية",
      action: () => navigate("/settings"),
      actionLabel: "الإعدادات",
    },
  ];

  const doneCount = steps.filter((s) => s.done).length;
  const pct = Math.round((doneCount / steps.length) * 100);

  return (
    <div
      className="bg-white border border-slate-100 rounded-2xl overflow-hidden mb-6 shadow-sm"
      data-testid="onboarding-checklist"
      dir="rtl"
    >
      {/* Header */}
      <div
        className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-[var(--bg-card-hover)] transition-colors"
        onClick={() => setCollapsed((c) => !c)}
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-[#004EEB] rounded-xl grid place-items-center">
            <Zap className="w-4 h-4 text-white" />
          </div>
          <div>
            <div className="text-sm font-bold text-slate-900">إعداد حسابك</div>
            <div className="text-xs text-slate-500">{doneCount} من {steps.length} خطوات مكتملة</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* Progress pill */}
          <div className="flex items-center gap-1.5">
            <div className="w-20 h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-[#004EEB] rounded-full transition-all duration-700"
                style={{ width: `${pct}%` }}
              />
            </div>
            <span className="text-xs font-semibold text-slate-500">{pct}%</span>
          </div>
          {collapsed
            ? <ChevronDown className="w-4 h-4 text-slate-400" />
            : <ChevronUp className="w-4 h-4 text-slate-400" />
          }
        </div>
      </div>

      {/* Steps */}
      {!collapsed && (
        <div className="border-t border-slate-100 divide-y divide-slate-50">
          {steps.map((step) => (
            <div
              key={step.key}
              className="flex items-center justify-between px-5 py-3"
            >
              <div className="flex items-center gap-3">
                {step.done
                  ? <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                  : <Circle className="w-4 h-4 text-slate-300 shrink-0" />
                }
                <span className={`text-sm ${step.done ? "text-slate-400 line-through" : "text-slate-700"}`}>
                  {step.label}
                </span>
              </div>
              {!step.done && (
                <button
                  onClick={step.action}
                  className="text-xs font-semibold text-[#004EEB] hover:underline"
                >
                  {step.actionLabel}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
